import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import { TaskConfig, TaskStep } from "graphql/types"
import { SyntheticEvent, useEffect, useState } from "react"
import { getTextForTaskStep } from "utils/taskSteps"

// Helper function to convert display text back to TaskStep enum
const getTaskStepFromText = (text: string): TaskStep => {
  const taskSteps = Object.values(TaskStep)
  return taskSteps.find((step) => getTextForTaskStep(step) === text) || TaskStep.NOTE
}

type AddTaskDialogProps = {
  open: boolean
  onClose: () => void
  formState: TaskConfig
  onSubmit: (arg: TaskConfig) => void
}

type ValidationErrors = {
  name?: string
  taskSteps?: string
}

const AddTaskDialog = (props: AddTaskDialogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const [localFormState, setLocalFormState] = useState<TaskConfig>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    if (!localFormState.taskSteps || localFormState.taskSteps.length === 0) {
      errors.taskSteps = "Mindestens ein Arbeitsschritt ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setLocalFormState((prevState) => ({ ...prevState, [name]: value }))

    // Clear validation error for this field
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name as keyof ValidationErrors]
        return newErrors
      })
    }
  }

  const handleTaskStepChange = (_: SyntheticEvent<Element, Event>, value: string[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      taskSteps: value.map((text) => getTaskStepFromText(text)),
    }))

    // Clear validation error for taskSteps
    if (validationErrors.taskSteps) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.taskSteps
        return newErrors
      })
    }
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {localFormState.id !== "NEW" ? "Arbeitsgang bearbeiten" : "Arbeitsgang hinzufügen"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <TextField
          size="small"
          name="name"
          label="Name"
          type="text"
          fullWidth
          value={localFormState.name}
          onChange={handleTextChange}
          sx={{ mt: 1 }}
          required
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />
        <TextField
          size="small"
          name="info"
          label="Beschreibung"
          type="text"
          fullWidth
          multiline
          value={localFormState.info}
          onChange={handleTextChange}
          sx={{ mt: 1 }}
        />

        <AutocompleteMulti
          options={Object.values(TaskStep).map((step) => getTextForTaskStep(step))}
          value={localFormState.taskSteps.map((step) => getTextForTaskStep(step))}
          onChange={handleTaskStepChange}
          label={"Arbeitsschritte"}
          isEnum={true}
          required
          error={!!validationErrors.taskSteps}
          helperText={validationErrors.taskSteps}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
      </DialogActions>
    </Dialog>
  )
}

export default AddTaskDialog
